import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import ResponsiveSidebar from '../components/layout/ResponsiveSidebar';
import { 
  BarChartIcon,
  FileIcon,
  UsersIcon,
  MapIcon,
  SettingsIcon,
  CheckCircleIcon,
  ClockIcon,
  AlertTriangleIcon,
  SearchIcon,
  FilterIcon,
  MenuIcon,
  CloseIcon
} from '../components/icons/Icons';

const AssignedIssues: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { t } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // Mock data for assigned issues
  const [allIssues, setAllIssues] = useState([
    {
      id: 'CIV-001',
      title: 'Pothole on Main Street',
      citizen: 'John Doe',
      category: 'Roads',
      status: 'In Progress',
      date: '2024-01-15',
      priority: 'High',
      location: 'Main Street, Sector 5',
      description: 'Large pothole causing traffic issues and vehicle damage',
      assignedTo: 'You',
      department: 'Public Works'
    },
    {
      id: 'CIV-002',
      title: 'Street Light Not Working',
      citizen: 'Jane Smith',
      category: 'Electricity',
      status: 'Pending',
      date: '2024-01-14',
      priority: 'Medium',
      location: 'Park Avenue, Block B',
      description: 'Street light has been off for a week, creating safety concerns',
      assignedTo: 'You',
      department: 'Electricity Department'
    },
    {
      id: 'CIV-003',
      title: 'Water Leak in Pipeline',
      citizen: 'Mike Johnson',
      category: 'Water Supply',
      status: 'Under Review',
      date: '2024-01-13',
      priority: 'High',
      location: 'Garden Road, Phase 2',
      description: 'Major water leak causing flooding and water wastage',
      assignedTo: 'You',
      department: 'Water Department'
    },
    {
      id: 'CIV-004',
      title: 'Garbage Collection Delay',
      citizen: 'Sarah Williams',
      category: 'Garbage Collection',
      status: 'Resolved',
      date: '2024-01-12',
      priority: 'Low',
      location: 'Oak Street, Area 3',
      description: 'Garbage has not been collected for 5 days',
      assignedTo: 'You',
      department: 'Sanitation Department'
    },
    {
      id: 'CIV-005',
      title: 'Broken Street Sign',
      citizen: 'Robert Brown',
      category: 'Roads',
      status: 'Pending',
      date: '2024-01-11',
      priority: 'Low',
      location: 'Elm Street, District 2',
      description: 'Traffic sign knocked down by vehicle, causing confusion',
      assignedTo: 'You',
      department: 'Public Works'
    },
    {
      id: 'CIV-006',
      title: 'Park Lighting Issue',
      citizen: 'Emily Davis',
      category: 'Electricity',
      status: 'In Progress',
      date: '2024-01-10',
      priority: 'Medium',
      location: 'Central Park, Zone A',
      description: 'Multiple lights not working in the park area',
      assignedTo: 'You',
      department: 'Electricity Department'
    }
  ]);

  const [filteredIssues, setFilteredIssues] = useState(allIssues);

  useEffect(() => {
    // Simulate loading data
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Filter issues based on search and filters
    let result = allIssues;
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(issue => 
        issue.title.toLowerCase().includes(term) ||
        issue.id.toLowerCase().includes(term) ||
        issue.citizen.toLowerCase().includes(term) ||
        issue.location.toLowerCase().includes(term)
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(issue => issue.status === statusFilter);
    }
    
    // Apply priority filter
    if (priorityFilter !== 'all') {
      result = result.filter(issue => issue.priority === priorityFilter);
    }
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      result = result.filter(issue => issue.category === categoryFilter);
    }
    
    setFilteredIssues(result);
  }, [searchTerm, statusFilter, priorityFilter, categoryFilter, allIssues]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Resolved': return 'text-green-600 bg-green-100';
      case 'In Progress': return 'text-orange-600 bg-orange-100';
      case 'Pending': return 'text-yellow-600 bg-yellow-100';
      case 'Under Review': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'text-red-600 bg-red-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleUpdateStatus = (issueId: string, newStatus: string) => {
    setAllIssues(prev => 
      prev.map(issue => 
        issue.id === issueId ? { ...issue, status: newStatus } : issue
      )
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setCategoryFilter('all');
  };

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/login');
    }
  };

  // Get unique categories for filter dropdown
  const categories = Array.from(new Set(allIssues.map(issue => issue.category)));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
              >
                {sidebarOpen ? <CloseIcon className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
              </button>
              <div className="flex items-center space-x-3">
                <img src="/hero.png" alt="Logo" className="w-8 h-8" />
                <h1 className="text-xl font-semibold text-gray-900">Assigned Issues</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900">{user?.full_name || user?.email}</p>
                <p className="text-xs text-gray-500">Official</p>
              </div>
              <button
                onClick={handleSignOut}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('auth.logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        <ResponsiveSidebar 
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          userRole="official"
          currentPage="assigned-issues"
        />

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Page Header */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Assigned Issues</h2>
                  <p className="text-gray-600 text-sm sm:text-base mt-1">Manage and track all issues assigned to your department</p>
                </div>
                <div className="sm:mt-0">
                  <button className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base">
                    Export Report
                  </button>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {/* Search */}
                <div className="lg:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
                  <div className="relative">
                    <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by title, ID, citizen, or location..."
                      className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                {/* Status Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="Pending">Pending</option>
                    <option value="Under Review">Under Review</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Resolved">Resolved</option>
                  </select>
                </div>
                
                {/* Priority Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Priority</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
                
                {/* Category Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 gap-3">
                <div className="text-xs text-gray-500">
                  Showing {filteredIssues.length} of {allIssues.length} issues
                </div>
                <button
                  onClick={clearFilters}
                  className="text-xs text-gray-600 hover:text-gray-900 self-start sm:self-auto"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Issues List - Mobile View */}
            <div className="block sm:hidden space-y-4">
              {filteredIssues.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                  <p className="text-gray-500">No issues found matching the current filters</p>
                </div>
              ) : (
                filteredIssues.map((issue) => (
                  <div key={issue.id} className="bg-white rounded-lg shadow-sm p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">{issue.title}</h3>
                        <p className="text-xs text-gray-500">#{issue.id}</p>
                      </div>
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(issue.status)}`}>
                        {issue.status}
                      </span>
                    </div>
                    
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center text-xs text-gray-600">
                        <span className="font-medium mr-2">Citizen:</span>
                        <span className="truncate">{issue.citizen}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <span className="font-medium mr-2">Category:</span>
                        <span>{issue.category}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <span className="font-medium mr-2">Location:</span>
                        <span className="truncate">{issue.location}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <span className="font-medium mr-2">Date:</span>
                        <span>{issue.date}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-xs mr-2">Priority:</span>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(issue.priority)}`}>
                          {issue.priority}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                      <button 
                        onClick={() => navigate(`/complaint/${issue.id}`)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        View Details
                      </button>
                      <select
                        value={issue.status}
                        onChange={(e) => handleUpdateStatus(issue.id, e.target.value)}
                        className="text-xs border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="Pending">Pending</option>
                        <option value="Under Review">Under Review</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Resolved">Resolved</option>
                      </select>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Issues Table - Desktop View */}
            <div className="hidden sm:block bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issue</th>
                      <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Citizen</th>
                      <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                      <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredIssues.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-4 sm:px-6 sm:py-4 text-center text-gray-500 text-sm">
                          No issues found matching the current filters
                        </td>
                      </tr>
                    ) : (
                      filteredIssues.map((issue) => (
                        <tr key={issue.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap">
                            <div>
                              <div className="text-xs sm:text-sm font-medium text-gray-900">{issue.title}</div>
                              <div className="text-xs text-gray-500">{issue.id}</div>
                              <div className="text-xs text-gray-400">{issue.location}</div>
                            </div>
                          </td>
                          <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">{issue.citizen}</td>
                          <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">{issue.category}</td>
                          <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(issue.status)}`}>
                              {issue.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(issue.priority)}`}>
                              {issue.priority}
                            </span>
                          </td>
                          <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">{issue.date}</td>
                          <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap text-xs sm:text-sm space-y-2">
                            <div className="flex flex-wrap gap-2">
                              <button 
                                onClick={() => navigate(`/complaint/${issue.id}`)}
                                className="text-blue-600 hover:text-blue-900 text-xs"
                              >
                                View Details
                              </button>
                              <select
                                value={issue.status}
                                onChange={(e) => handleUpdateStatus(issue.id, e.target.value)}
                                className="text-xs border border-gray-300 rounded px-2 py-1"
                              >
                                <option value="Pending">Pending</option>
                                <option value="Under Review">Under Review</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Resolved">Resolved</option>
                              </select>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <FileIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-600">Total Issues</p>
                    <p className="text-lg font-bold text-gray-900">{allIssues.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center">
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <AlertTriangleIcon className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-600">In Progress</p>
                    <p className="text-lg font-bold text-gray-900">
                      {allIssues.filter(issue => issue.status === 'In Progress').length}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <CheckCircleIcon className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-600">Resolved</p>
                    <p className="text-lg font-bold text-gray-900">
                      {allIssues.filter(issue => issue.status === 'Resolved').length}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <AlertTriangleIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-600">High Priority</p>
                    <p className="text-lg font-bold text-gray-900">
                      {allIssues.filter(issue => issue.priority === 'High').length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AssignedIssues;